(() => {
  if (window.CaseBangSnapMenu) {
    window.CaseBangSnapMenu.init();
    return;
  }

  const panels = new Map();
  let active = null;
  let previousOverflow = '';
  let previousScrollbarGutter = '';

  function setExpanded(entry, expanded) {
    document.querySelectorAll('[data-cbsm-open]').forEach((trigger) => {
      if (trigger.dataset.cbsmOpen === entry.dialog.id) trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  function resetSubmission(entry) {
    window.clearTimeout(entry.submitTimer);
    entry.dialog.querySelectorAll('[data-cbsm-language-form]').forEach((form) => {
      form.removeAttribute('aria-busy');
      const status = form.querySelector('[data-cbsm-language-status]');
      if (status) status.textContent = '';
    });
  }

  function showView(entry, name, focus = true) {
    const views = {
      main: entry.dialog.querySelector('[data-cbsm-main]'),
      devices: entry.dialog.querySelector('[data-cbsm-devices]'),
      languages: entry.dialog.querySelector('[data-cbsm-languages]')
    };
    if (!views[name]) name = 'main';
    Object.entries(views).forEach(([view, element]) => { if (element) element.hidden = view !== name; });
    entry.view = name;
    entry.dialog.querySelector('[data-cbsm-device-open]')?.setAttribute('aria-expanded', String(name === 'devices'));
    entry.dialog.querySelector('[data-cbsm-language-open]')?.setAttribute('aria-expanded', String(name === 'languages'));
    entry.scroll.scrollTop = 0;
    if (focus && name !== 'main') views[name].querySelector('[tabindex="-1"]')?.focus({ preventScroll: true });
  }

  function returnFromLanguage(entry) {
    const previous = entry.languageReturn;
    showView(entry, previous.view, false);
    entry.scroll.scrollTop = previous.scroll;
    entry.dialog.querySelector('[data-cbsm-language-open]')?.focus({ preventScroll: true });
  }

  function returnFromDevices(entry) {
    showView(entry, 'main', false);
    entry.scroll.scrollTop = entry.mainScroll;
    entry.dialog.querySelector('[data-cbsm-device-open]')?.focus({ preventScroll: true });
  }

  function finishClose(entry) {
    if (entry.closeHandled) return;
    entry.closeHandled = true;
    setExpanded(entry, false);
    showView(entry, 'main', false);
    resetSubmission(entry);
    if (active === entry) {
      active = null;
      document.documentElement.style.overflow = previousOverflow;
      document.documentElement.style.scrollbarGutter = previousScrollbarGutter;
    }
    if (entry.restoreFocus && entry.opener?.isConnected) entry.opener.focus({ preventScroll: true });
    entry.restoreFocus = true;
  }

  function close(entry, restoreFocus = true) {
    entry.restoreFocus = restoreFocus;
    if (entry.dialog.open) {
      entry.dialog.close();
      finishClose(entry);
    }
  }

  function open(entry, trigger) {
    if (active && active !== entry) close(active, false);
    if (entry.dialog.open) {
      close(entry);
      return;
    }
    entry.opener = trigger;
    entry.restoreFocus = true;
    entry.closeHandled = false;
    entry.mainScroll = 0;
    entry.languageReturn = { view: 'main', scroll: 0 };
    showView(entry, 'main', false);
    resetSubmission(entry);
    previousOverflow = document.documentElement.style.overflow;
    previousScrollbarGutter = document.documentElement.style.scrollbarGutter;
    // Preserve the classic scrollbar's space while the page is locked.
    if (window.innerWidth - document.documentElement.clientWidth > 0) {
      document.documentElement.style.scrollbarGutter = 'stable';
    }
    document.documentElement.style.overflow = 'hidden';
    active = entry;
    entry.dialog.showModal();
    setExpanded(entry, true);
    entry.dialog.querySelector('[data-cbsm-close]')?.focus({ preventScroll: true });
  }

  function remove(entry) {
    if (entry.dialog.open) close(entry, false);
    finishClose(entry);
    resetSubmission(entry);
    entry.abort.abort();
    entry.dialog.remove();
    panels.delete(entry.sectionId);
  }

  function onLanguageSubmit(entry, event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cbsm-language-form]')) return;
    const button = event.submitter || document.activeElement;
    if (!button?.matches('[data-cbsm-language-option]')) return;
    if (form.getAttribute('aria-busy') === 'true') {
      event.preventDefault();
      return;
    }
    if (button.value === form.dataset.currentLanguage) {
      event.preventDefault();
      returnFromLanguage(entry);
      return;
    }
    const current = new URL(window.location.href);
    ['locale', 'locale_code', 'language', 'language_code'].forEach((key) => current.searchParams.delete(key));
    let returnTo = form.querySelector('input[name="return_to"]');
    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.append(returnTo);
    }
    returnTo.value = current.pathname + current.search + current.hash;
    form.setAttribute('aria-busy', 'true');
    const status = form.querySelector('[data-cbsm-language-status]');
    if (status) status.textContent = status.dataset.loadingText || '';
    window.clearTimeout(entry.submitTimer);
    entry.submitTimer = window.setTimeout(() => resetSubmission(entry), 15000);
  }

  function init() {
    document.querySelectorAll('[data-cbsm-root]').forEach((root) => {
      const sectionId = root.dataset.cbsmSection;
      const old = panels.get(sectionId);
      if (old?.root === root) return;
      if (old) remove(old);
      const dialog = root.querySelector('[data-cbsm-panel]');
      const scroll = dialog?.querySelector('[data-cbsm-scroll]');
      if (!dialog || !scroll || typeof dialog.showModal !== 'function') return;
      const entry = { root, dialog, scroll, sectionId, view: 'main', mainScroll: 0, languageReturn: { view: 'main', scroll: 0 }, opener: null, restoreFocus: true, closeHandled: true, abort: new AbortController() };
      const options = { signal: entry.abort.signal };
      panels.set(sectionId, entry);
      document.body.appendChild(dialog);
      dialog.addEventListener('close', () => { if (!dialog.open) finishClose(entry); }, options);
      dialog.addEventListener('cancel', (event) => {
        if (entry.view === 'languages') {
          event.preventDefault();
          returnFromLanguage(entry);
        } else if (entry.view === 'devices') {
          event.preventDefault();
          returnFromDevices(entry);
        }
      }, options);
      dialog.addEventListener('submit', (event) => onLanguageSubmit(entry, event), options);
      dialog.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.closest('[data-cbsm-studio]')) {
          event.preventDefault();
          const studio = window.CasebangStudio;
          if (!studio || studio.version !== 1) return;
          const returnFocus = entry.opener;
          // Native close leaves the top layer and restores this menu's scroll
          // lock synchronously before Studio takes ownership of the viewport.
          close(entry, false);
          studio.open({ returnFocus });
          return;
        }
        if (target.closest('[data-cbsm-close]')) {
          close(entry);
          return;
        }
        if (target.closest('[data-cbsm-loyalty]')) {
          close(entry);
          return;
        }
        if (target.closest('[data-cbsm-device-open]')) {
          entry.mainScroll = entry.scroll.scrollTop;
          showView(entry, 'devices');
          return;
        }
        if (target.closest('[data-cbsm-back]')) {
          returnFromDevices(entry);
          return;
        }
        if (target.closest('[data-cbsm-language-open]')) {
          if (entry.view === 'languages') returnFromLanguage(entry);
          else {
            entry.languageReturn = { view: entry.view, scroll: entry.scroll.scrollTop };
            showView(entry, 'languages');
          }
          return;
        }
        if (target.closest('[data-cbsm-language-back]')) {
          returnFromLanguage(entry);
          return;
        }
        if (target === dialog) {
          const bounds = dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close(entry);
        }
      }, options);
    });
    for (const entry of panels.values()) {
      if (!entry.root.isConnected) remove(entry);
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-cbsm-open]');
    if (!trigger) return;
    const entry = [...panels.values()].find((candidate) => candidate.dialog.id === trigger.dataset.cbsmOpen);
    if (!entry) return;
    event.preventDefault();
    open(entry, trigger);
  });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const entry of panels.values()) {
      if (event.target.contains(entry.root)) remove(entry);
    }
  });
  window.addEventListener('pageshow', () => {
    init();
    for (const entry of panels.values()) resetSubmission(entry);
  });
  window.CaseBangSnapMenu = { init };
  init();
})();
